import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { DROPDOWN_AUTOFOCUS_DELAY_MS } from '../../lib/config'

export interface MultiSelectOption {
  /** Unique key — used as the selection value */
  value: string
  label: string
  icon?: string
  color?: string
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[]
  selected: string[]                  // array of option.value strings
  onChange: (selected: string[]) => void
  placeholder?: string                // trigger button label when nothing selected
  searchPlaceholder?: string          // input placeholder inside dropdown
  maxHeight?: number                  // max height of options list in px
  align?: 'left' | 'right'           // which side the dropdown panel opens towards
  fullWidth?: boolean                 // stretch trigger and panel to fill container
}

export function MultiSelectDropdown({
  options,
  selected,
  onChange,
  placeholder = 'Filter',
  searchPlaceholder = 'Search...',
  maxHeight = 180,
  align = 'right',
  fullWidth = false,
}: MultiSelectDropdownProps) {
  const [open, setOpen]       = useState(false)
  const [search, setSearch]   = useState('')
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const containerRef          = useRef<HTMLDivElement>(null)
  const panelRef              = useRef<HTMLDivElement>(null)
  const searchRef             = useRef<HTMLInputElement>(null)

  const calcPos = useCallback(() => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    setPanelPos({
      top:   rect.bottom + 4,
      left:  align === 'left' ? rect.left : rect.right - (fullWidth ? rect.width : 210),
      width: fullWidth ? rect.width : 210,
    })
  }, [align, fullWidth])

  // Close on outside click — panel is portalled so check both refs
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      const inTrigger = containerRef.current?.contains(target)
      const inPanel   = panelRef.current?.contains(target)
      if (!inTrigger && !inPanel) { setOpen(false); setSearch('') }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Recalculate position on scroll / resize while open
  useEffect(() => {
    if (!open) return
    const update = () => calcPos()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, calcPos])

  const handleOpen = () => {
    calcPos()
    setOpen(p => {
      if (!p) setTimeout(() => searchRef.current?.focus(), DROPDOWN_AUTOFOCUS_DELAY_MS)
      return !p
    })
  }

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  )

  const hasSelection = selected.length > 0

  const panel = open && panelPos && createPortal(
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: panelPos.top,
        left: panelPos.left,
        width: panelPos.width,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        zIndex: 20000, padding: '0.3rem',
      }}
    >
      {/* Search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.35rem',
        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
        borderRadius: '7px', padding: '0.3rem 0.5rem', marginBottom: '0.25rem',
      }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          ref={searchRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--text-primary)', fontSize: '0.72rem', fontFamily: 'inherit',
          }}
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.65rem', padding: 0, lineHeight: 1 }}
          >✕</button>
        )}
      </div>

      {/* Options list */}
      <div style={{ maxHeight, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '0.5rem 0.65rem', fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            No results found
          </div>
        ) : filtered.map(opt => {
          const isSelected = selected.includes(opt.value)
          const color = opt.color ?? '#7c3aed'
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              style={{
                width: '100%', textAlign: 'left', padding: '0.35rem 0.5rem',
                borderRadius: '7px', border: 'none', cursor: 'pointer',
                background: isSelected ? `${color}18` : 'transparent',
                color: isSelected ? color : 'var(--text-secondary)',
                fontSize: '0.78rem',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-elevated)'
              }}
              onMouseLeave={e => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'
              }}
            >
              {/* Checkbox */}
              <div style={{
                width: 14, height: 14, borderRadius: '4px', flexShrink: 0,
                border: `1.5px solid ${isSelected ? color : 'var(--border)'}`,
                background: isSelected ? color : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.1s',
              }}>
                {isSelected && (
                  <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                    <polyline points="1.5,5 4,7.5 8.5,2" stroke="white" strokeWidth="1.5"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              {opt.icon && <span style={{ fontSize: '0.85rem' }}>{opt.icon}</span>}
              <span style={{ fontWeight: isSelected ? 600 : 400 }}>{opt.label}</span>
            </button>
          )
        })}
      </div>

      {/* Footer */}
      {hasSelection && (
        <div style={{
          borderTop: '1px solid var(--border)',
          marginTop: '0.25rem',
          padding: '0.25rem 0.5rem 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
            {selected.length} selected
          </span>
          <button
            type="button"
            onClick={() => onChange([])}
            style={{
              background: 'none', border: 'none', color: 'var(--accent-primary)',
              cursor: 'pointer', fontSize: '0.68rem', fontWeight: 600, padding: 0,
            }}
          >Clear all</button>
        </div>
      )}
    </div>,
    document.body
  )

  return (
    <div ref={containerRef} style={{ position: 'relative', flexShrink: 0, ...(fullWidth && { width: '100%' }) }}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleOpen}
        title={placeholder}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.3rem',
          padding: '0.3rem 0.55rem', borderRadius: '8px', fontSize: '0.72rem',
          cursor: 'pointer',
          background: hasSelection ? 'rgba(124,58,237,0.15)' : 'var(--bg-elevated)',
          border: `1px solid ${hasSelection ? 'rgba(124,58,237,0.5)' : 'var(--border)'}`,
          color: hasSelection ? '#a78bfa' : 'var(--text-muted)',
          transition: 'all 0.15s',
          ...(fullWidth && { width: '100%' }),
        }}
      >
        {/* Funnel icon */}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="6" x2="20" y2="6"/>
          <line x1="8" y1="12" x2="16" y2="12"/>
          <line x1="11" y1="18" x2="13" y2="18"/>
        </svg>
        <span style={{ fontWeight: hasSelection ? 600 : 400, flex: fullWidth ? 1 : undefined, textAlign: 'left' }}>
          {hasSelection
            ? `${selected.length} selected`
            : placeholder}
        </span>
        <span style={{ opacity: 0.6, fontSize: '0.6rem' }}>▼</span>
      </button>

      {panel}
    </div>
  )
}
